// RULE-01 規則範本：依家戶的規則目錄（每台可當觸發的事件、可下的命令）挑設備，組成規則（格式見 plan ADR-011）。
// 「彈窗」是 VMS 的設備關聯（device_links purpose popup），不是規則動作；錄影用攝影機的手動錄影命令（有這個命令才加）。
import type { CatalogThing, RuleSpec } from '../../lib/rulesApi';

export type TemplateId = 'door_record_notify' | 'armed_pir_siren' | 'ai_person_notify';

/** 門窗開：磁簧類的狀態事件。 */
export function contactEvent(t: CatalogThing): string | undefined {
  return t.events.find((e) => /boolean_state\.state_change|kinio_contact|contact/.test(e));
}
/** 有人 / 移動：PIR、佔用。 */
export function motionEvent(t: CatalogThing): string | undefined {
  return t.events.find((e) => /occupancy|kinio_pir|kinio_motion\.motion/.test(e));
}
/** 攝影機的人形偵測事件。 */
export function personEvent(t: CatalogThing): string | undefined {
  return t.events.find((e) => e === 'kinio_object_recognition.object_detected');
}
export const RECORD_CMD = 'kinio_recording.manual_record_start';
export const SIREN_CMD = 'kinio_siren.trigger';

/** 每個範本要挑的設備角色，與哪些設備符合。 */
export function candidates(id: TemplateId, things: CatalogThing[]): Record<string, CatalogThing[]> {
  switch (id) {
    case 'door_record_notify':
      return { sensor: things.filter((t) => contactEvent(t)), camera: things.filter((t) => t.commands.includes(RECORD_CMD)) };
    case 'armed_pir_siren':
      return { sensor: things.filter((t) => motionEvent(t)), siren: things.filter((t) => t.commands.includes(SIREN_CMD)) };
    case 'ai_person_notify':
      return { camera: things.filter((t) => personEvent(t)) };
  }
}

/** 範本一定要有的設備角色；這個家戶沒有符合的設備時回那個角色（空狀態的範本卡寫「這個範本需要…，這個家戶還沒有」）。 */
export function missingRole(id: TemplateId, things: CatalogThing[]): string | null {
  const req = id === 'ai_person_notify' ? 'camera' : 'sensor';
  return candidates(id, things)[req]?.length ? null : req;
}

/**
 * 範本 → 規則。picks = 角色 → device_ref（可選的角色沒挑就不加那個動作）；notify = 家戶開了規則通知（catalog action_types 有 notify）。
 * 回 null = 必要的角色沒挑。
 */
export function buildTemplate(
  id: TemplateId,
  name: string,
  picks: Record<string, string | undefined>,
  things: CatalogThing[],
  notify: boolean,
  text: string,
): RuleSpec | null {
  const th = (device_ref?: string) => things.find((t) => t.device_ref === device_ref);
  const notifyAct = notify ? [{ type: 'notify', text }] : [];
  switch (id) {
    case 'door_record_notify': {
      const s = th(picks.sensor);
      const ev = s && contactEvent(s);
      if (!s || !ev) return null;
      const cam = th(picks.camera);
      const actions = [...(cam ? [{ type: 'command', device_ref: cam.device_ref, id: RECORD_CMD }] : []), ...notifyAct];
      return actions.length
        ? { kind: 'automation', name, mode: 'single', triggers: [{ type: 'event', device_ref: s.device_ref, event: ev }], conditions: [], actions }
        : null;
    }
    case 'armed_pir_siren': {
      const s = th(picks.sensor);
      const ev = s && motionEvent(s);
      if (!s || !ev) return null;
      const siren = th(picks.siren);
      const actions = [...(siren ? [{ type: 'command', device_ref: siren.device_ref, id: SIREN_CMD }] : []), ...notifyAct];
      return actions.length
        ? {
            kind: 'automation',
            name,
            mode: 'single',
            triggers: [{ type: 'event', device_ref: s.device_ref, event: ev }],
            conditions: [{ type: 'armed_mode', in: ['away', 'night'] }],
            actions,
          }
        : null;
    }
    case 'ai_person_notify': {
      const c = th(picks.camera);
      const ev = c && personEvent(c);
      if (!c || !ev || !notify) return null;
      return {
        kind: 'automation',
        name,
        mode: 'single',
        triggers: [{ type: 'event', device_ref: c.device_ref, event: ev, where: { object_type: 'person' } }],
        conditions: [],
        actions: notifyAct,
      };
    }
  }
}
